"use client"
import { useState, useEffect, useContext } from "react"
import { IconButton, Modal, Typography, Box, Paper, Grid, Button, Card, CardContent, alpha, Zoom } from "@mui/material"

// Icons
import { Close as CloseIcon } from "@mui/icons-material"
import DescriptionIcon from "@mui/icons-material/Description"
import PersonIcon from "@mui/icons-material/Person"
import BusinessIcon from "@mui/icons-material/Business"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import PaymentIcon from "@mui/icons-material/Payment"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import StoreIcon from "@mui/icons-material/Store"
import AccountCircleIcon from "@mui/icons-material/AccountCircle"
import HomeIcon from "@mui/icons-material/Home"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import ImageIcon from "@mui/icons-material/Image"
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"
import DownloadIcon from "@mui/icons-material/Download"

import { ErpContext } from "../../context/ErpContext.js"
import SpinnerLading from "../misc/SpinnerComponent.js"

const ModalViewTablaEgreso = ({ openModal, handleCloseModal, selectedTransaccion }) => {
  const [selectedTransaccionEntidad, setSelectedTransaccionEntidad] = useState(null)
  const [calculatedTotal, setCalculatedTotal] = useState(0)
  const [totalMontoDescuento, setTotalMontoDescuento] = useState(0)
  const [porcentajeDescuento, setPorcentajeDescuento] = useState(0)
  const [montoSinImpuesto, setMontoSinImpuesto] = useState(0)
  const [loading, setLoading] = useState(true)
  const [archivosAdjuntos, setArchivosAdjuntos] = useState([])

  const { getEntidadByID, getItemById ,isNavbarCollapsed,
  } = useContext(ErpContext)

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (selectedTransaccion) {
          // Obtener la entidad cliente solo si existe un ID válido
          if (selectedTransaccion.idEntidadNumero && selectedTransaccion.idEntidadNumero !== null) {
            try {
          const entidadCliente = await getEntidadByID(selectedTransaccion.idEntidadNumero)
          setSelectedTransaccionEntidad(entidadCliente)
            } catch (error) {
              console.error("Error al obtener entidad:", error)
              setSelectedTransaccionEntidad(null)
            }
          } else {
            setSelectedTransaccionEntidad(null)
          }

          // Calcular montos
          const { montoTotal, impuestos = [], montoDescuento = 0, porcentajeDescuento = 0 } = selectedTransaccion

          // Calcular el monto total sin impuestos
          const totalSinImpuestos = impuestos.reduce((total, impuesto) => {
            return total - (impuesto.montoTotal || 0)
          }, montoTotal)

          // Aplicar descuentos
          const totalSinImpuestosYDescuento = totalSinImpuestos + montoDescuento
          const totalFinal = totalSinImpuestosYDescuento * (1 - porcentajeDescuento / 100)

          // Actualizar estados
          setCalculatedTotal(totalFinal)
          setTotalMontoDescuento(montoDescuento)
          setPorcentajeDescuento(porcentajeDescuento)
          setMontoSinImpuesto(totalSinImpuestos)

          // Cargar archivos adjuntos si existen
          if (selectedTransaccion.archivosAdjuntos) {
            try {
              const archivos = typeof selectedTransaccion.archivosAdjuntos === 'string' 
                ? JSON.parse(selectedTransaccion.archivosAdjuntos)
                : selectedTransaccion.archivosAdjuntos
              setArchivosAdjuntos(Array.isArray(archivos) ? archivos : [])
            } catch (error) {
              console.error("Error al parsear archivos adjuntos:", error)
              setArchivosAdjuntos([])
            }
          } else {
            setArchivosAdjuntos([])
          }
        }
      } catch (error) {
        console.error("Error al cargar los datos:", error)
      } finally {
        setLoading(false) // Ocultar el spinner independientemente del resultado
      }
    }

    fetchData()
  }, [selectedTransaccion])

  // Renderiza el spinner mientras está cargando
  if (loading) {
    return <SpinnerLading />
  }

  return (
    <Modal
      open={openModal}
      onClose={handleCloseModal}
      aria-labelledby="modal-transaccion-titulo"
      aria-describedby="modal-transaccion-descripcion"
    >
      <Box
        className="modal-background"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "100%",
          maxHeight: "90vh",
        }}
      >
        <Zoom in={openModal} timeout={300}>
          <Paper className="modal-container">
            {/* Header */}
            <Box
              sx={{
                bgcolor: "#5b5b5f",
                color: "white",
                p: 2,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ReceiptLongIcon />
                <Typography
                  variant="h6"
                  component="h2"
                  className="modal-title"
                  sx={{ color: "white", borderBottom: "none", m: 0, p: 0 }}
                >
                  Detalles de la Transacción #{selectedTransaccion?.id}
                </Typography>
              </Box>
              <IconButton
                onClick={handleCloseModal}
                sx={{ color: "white" }}
                className="close-button"
                aria-label="cerrar"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3 }}>
              {/* Información principal */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Datos ERP */}
                <Grid item xs={12} md={3}>
                  <Card
                    elevation={2}
                    className="contenedorDatosModal"
                    sx={{
                      height: "100%",
                      borderLeft: "4px solid #5b5b5f",
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        className="section-title"
                        sx={{
                          mb: 2,
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <BusinessIcon sx={{ color: "#5b5b5f" }} />
                        Datos ERP
                      </Typography>

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CalendarTodayIcon fontSize="small" color="action" />
                          <Typography variant="body2" className="transaction-detail-label">
                            Fecha:
                          </Typography>
                          <Typography variant="body2" className="transaction-detail-value">
                            {selectedTransaccion?.createdAt || "N/A"}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <DescriptionIcon fontSize="small" color="action" />
                          <Typography variant="body2" className="transaction-detail-label">
                            Tipo:
                          </Typography>
                          <Typography variant="body2" className="transaction-detail-value">
                            {selectedTransaccion?.idTipoTransaccion || "N/A"}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <StoreIcon fontSize="small" color="action" />
                          <Typography variant="body2" className="transaction-detail-label">
                            Negocio:
                          </Typography>
                          <Typography variant="body2" className="transaction-detail-value">
                            {selectedTransaccion?.negocio || "N/A"}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <AccountCircleIcon fontSize="small" color="action" />
                          <Typography variant="body2" className="transaction-detail-label">
                            Usuario:
                          </Typography>
                          <Typography variant="body2" className="transaction-detail-value">
                            {selectedTransaccion?.usuario || "N/A"}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <LocationOnIcon fontSize="small" color="action" />
                          <Typography variant="body2" className="transaction-detail-label">
                            Ubicación:
                          </Typography>
                          <Typography variant="body2" className="transaction-detail-value">
                            {selectedTransaccion?.ubicacion || "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Datos Entidad */}
                <Grid item xs={12} md={3}>
                  <Card
                    elevation={2}
                    className="contenedorDatosModal"
                    sx={{
                      height: "100%",
                      borderLeft: "4px solid rgb(244, 177, 14)",
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        className="section-title"
                        sx={{
                          mb: 2,
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <PersonIcon sx={{ color: "rgb(244, 177, 14)" }} />
                        Datos de la Entidad
                      </Typography>

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2" className="transaction-detail-label">
                            Nombre:
                          </Typography>
                          <Typography variant="body2" className="transaction-detail-value">
                            {selectedTransaccion?.descripcionEntidad} {selectedTransaccion?.apellido || ""}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <DescriptionIcon fontSize="small" color="action" />
                          <Typography variant="body2" className="transaction-detail-label">
                            DNI/CUIT:
                          </Typography>
                          <Typography variant="body2" className="transaction-detail-value">
                            {selectedTransaccion?.dniCuitCuil || "N/A"}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <HomeIcon fontSize="small" color="action" />
                          <Typography variant="body2" className="transaction-detail-label">
                            Dirección:
                          </Typography>
                          <Typography variant="body2" className="transaction-detail-value">
                            {selectedTransaccion?.direccion || "N/A"}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <LocationOnIcon fontSize="small" color="action" />
                          <Typography variant="body2" className="transaction-detail-label">
                            Localidad:
                          </Typography>
                          <Typography variant="body2" className="transaction-detail-value">
                            {selectedTransaccion?.localidad || "N/A"}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <LocationOnIcon fontSize="small" color="action" />
                          <Typography variant="body2" className="transaction-detail-label">
                            Provincia:
                          </Typography>
                          <Typography variant="body2" className="transaction-detail-value">
                            {selectedTransaccion?.provincia || "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Datos Financieros */}
                <Grid item xs={12} md={3}>
                  <Card
                    elevation={2}
                    className="contenedorDatosModal"
                    sx={{
                      height: "100%",
                      borderLeft: "4px solid #4CAF50",
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        className="section-title"
                        sx={{
                          mb: 2,
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <AttachMoneyIcon sx={{ color: "#4CAF50" }} />
                        Datos Financieros
                      </Typography>

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {/* Total */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 1,
                            bgcolor: alpha("#4CAF50", 0.1),
                            borderRadius: 1,
                          }}
                        >
                          <AttachMoneyIcon fontSize="small" sx={{ color: "#4CAF50" }} />
                          <Typography variant="body1" className="transaction-detail-label" fontWeight="bold">
                            Total:
                          </Typography>
                          <Typography variant="body1" className="value">
                            {new Intl.NumberFormat("es-AR", {
                              style: "currency",
                              currency: "ARS",
                            }).format(selectedTransaccion?.montoTotal || 0)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Medios de Pago */}
                <Grid item xs={12} md={3}>
                  <Card
                    elevation={2}
                    className="contenedorDatosModal"
                    sx={{
                      height: "100%",
                      borderLeft: "4px solid #2196F3",
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        className="section-title"
                        sx={{
                          mb: 2,
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <PaymentIcon sx={{ color: "#2196F3" }} />
                        Medios de Pago
                      </Typography>

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {selectedTransaccion?.pagos && selectedTransaccion.pagos.length > 0 ? (
                          selectedTransaccion.pagos.map((pago, idx) => (
                            <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <PaymentIcon fontSize="small" color="action" />
                              <Typography variant="body2" className="transaction-detail-label">
                                {pago.descripcion}:
                              </Typography>
                              <Typography variant="body2" className="value">
                                {new Intl.NumberFormat("es-AR", {
                                  style: "currency",
                                  currency: "ARS",
                                }).format(pago.monto || 0)}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                            No hay información de pagos
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Descripción */}
              {selectedTransaccion.descripcion && (
                <Card
                  elevation={2}
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    borderLeft: "4px solid #5b5b5f",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <DescriptionIcon sx={{ color: "#5b5b5f" }} />
                    Descripción
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransaccion.afectaCuentaCorriente ? (
                      <>
                        <span style={{ color: "#4CAF50", fontWeight: "bold" }}>
                          [CC]
                        </span>
                        <span style={{ color: "black" }}> {selectedTransaccion.descripcion}</span>
                      </>
                    ) : (
                      selectedTransaccion.descripcion
                    )}
                  </Typography>
                </Card>
              )}

              {/* Archivos Adjuntos */}
              {archivosAdjuntos.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "#5b5b5f",
                    }}
                  >
                    <InsertDriveFileIcon />
                    Archivos Adjuntos
                  </Typography>
                  <Grid container spacing={2}>
                    {archivosAdjuntos.map((archivo, index) => {
                      const isImage = archivo.tipo?.startsWith('image/')
                      const isPDF = archivo.tipo === 'application/pdf'
                      const base64Data = `data:${archivo.tipo};base64,${archivo.base64}`

                      return (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Card
                            elevation={2}
                            sx={{
                              borderRadius: 2,
                              overflow: "hidden",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                                transform: "translateY(-2px)",
                              },
                            }}
                          >
                            {isImage && (
                              <Box
                                component="img"
                                src={base64Data}
                                alt={archivo.nombre}
                                sx={{
                                  width: "100%",
                                  height: 200,
                                  objectFit: "cover",
                                  cursor: "pointer",
                                }}
                                onClick={() => window.open(base64Data, '_blank')}
                              />
                            )}
                            {isPDF && (
                              <Box
                                sx={{
                                  width: "100%",
                                  height: 200,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: alpha("#f44336", 0.1),
                                }}
                              >
                                <PictureAsPdfIcon sx={{ fontSize: 64, color: "#f44336" }} />
                              </Box>
                            )}
                            <CardContent>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  mb: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {archivo.nombre}
                              </Typography>
                              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<DownloadIcon />}
                                  onClick={() => {
                                    const link = document.createElement('a')
                                    link.href = base64Data
                                    link.download = archivo.nombre
                                    link.click()
                                  }}
                                  fullWidth
                                >
                                  Descargar
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      )
                    })}
                  </Grid>
                </Box>
              )}

              {/* Acciones */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  borderTop: "1px solid #eee",
                  pt: 2,
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdfIcon />}
                  sx={{
                    bgcolor: "#5b5b5f",
                    "&:hover": {
                      bgcolor: alpha("#5b5b5f", 0.9),
                    },
                  }}
                >
                  Generar PDF
                </Button>
              </Box>
            </Box>
          </Paper>
        </Zoom>
      </Box>
    </Modal>
  )
}

export default ModalViewTablaEgreso
